import { forwardRef, useEffect } from 'react';
import { inquiryType, gtmEventType, contactType } from '@Components/Utilities/SandhillsConstants';
import { gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import TranslationText from '@Components/Utilities/TranslationText';
import PhoneLink from '@Components/Shared/Phone/PhoneLink';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import Icon from '@mui/material/Icon';
import { useRecoilValue } from 'recoil';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { ThemedLinkButton } from '@Components/MuiWrappers/Button';
import { ThemeProvider } from '@mui/material/styles';
import { sandhillsRequest } from '../Utilities/SandhillsUtility';
import { ThemedBox } from '@Components/MuiWrappers/ThemedBox';
import { ThemedLink } from '@Components/MuiWrappers/ThemedLink';
import Typography from '@mui/material/Typography';

const CollapsibleContacts = loadable(() => import('@Components/Contact/CollapsibleContacts'));
const DealerInfo = forwardRef((props, ref) => {
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const currentUser = useRecoilValue(atoms.currentUser);
    const theme = useRecoilValue(atoms.muiThemes);
    const routes = useRecoilValue(atoms.routes);

    const phoneClickToTrack = (e, phoneNumber, phoneType) => {
        if (!currentUser.IsSearchEngine) {
            let element = e.target;
            sandhillsRequest({
                url: routes.PhoneClickToTrackURL.replace("{0}", e.id).replace("{1}", "1"),
                method: 'post',
                data: {
                    PhoneNumber: phoneNumber,
                    PhoneType: phoneType
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then(() => {
                element.setAttribute('data-impression-logged', 'true');
            }).catch(() => {
                element.setAttribute('data-impression-logged', 'false');
            });
        }
    };

    const isRetailContact = props.contactType == contactType.Retail && props.listingType != "For Charter";

    return (
        <ThemeProvider theme={(!theme.isHostedSite && theme.mainMode == "dark") ? theme.darkTheme : theme.lightTheme}>
            <ThemedBox>
                <div className={'dealer-contact dealer-contact_' + props.contactType}>
                    <div className='dealer-contact__header'>
                        <Typography variant='h2' className='dealer-contact__heading' dangerouslySetInnerHTML={{ __html: props.ContactHeaderText }} sx={{ fontWeight: 'bold' }}></Typography>
                        {(props.viewAuctioneerLink?.Url && (currentSite?.Site?.IsBidcaller || currentSite?.Site?.IsAuctionTime)) &&
                            <>
                                <ThemedLink variant='text' size='small' href={props.viewAuctioneerLink?.Url} underline='hover' className='dealer-contact__view-seller-info' sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                                    <TranslationText tKey='sandhills.viewauctioneerinformation' />
                                    <Icon baseClassName='fas' className='fa-external-link-alt' />
                                </ThemedLink>
                            </>}
                        {(isRetailContact && props.AllListingsForContactURL && !props.isAuction) &&
                            <>
                                <ThemedLink variant='text' size='small' href={props.AllListingsForContactURL} underline='hover' className='dealer-contact__view-seller-info' onClick={() => gtmPushEvent(gtmEventType.sellerInfoClick)} sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                                {currentSite?.Site?.IsAuctionTime
                                    ? <TranslationText tKey='sandhills.auctioneerinformation' />
                                    : <TranslationText tKey='sandhills.viewsellerinformation' />}
                                    <Icon baseClassName='fas' className='fa-external-link-alt' />
                                </ThemedLink>
                            </>}
                    </div>

                    <div className='dealer-contact__details'>
                        <div className='dealer-contact__details_left-col'>
                            <div className='dealer-contact__branch-name'>
                                <Typography variant='body2' className='dealer-contact__branch-name'>{props.CompanyName}</Typography>
                            </div>
                            {props.ContactName &&
                                <div className='dealer-contact__name'>
                                    <Typography variant='body2' className='dealer-contact__name'><span className='dealer-contact__label'><TranslationText tKey='sandhills.tradepubwebsite.mvc.contact' />:</span>{props.ContactName}</Typography>
                                </div>
                            }
                            {(props.ShowContactInfo && props.ShowDealerPhoneNumber) && props.UnformattedPhone && 
                                <div className='dealer-contact__phone'>
                                    <PhoneLink {...props}
                                        Manufacturer={props.manufacturer}
                                        Model={props.model}
                                        CategoryID={props.categoryID}
                                        ListingType={props.listingType}
                                        ListingID={props.listingID}
                                        PartID={props.partID}
                                        Condition={props.condition}
                                        isMobile={props.windowSize.isMobile}
                                        showPhoneLabel={true}
                                        showTextLabel={true}
                                    />
                                </div>
                            }
                            {(props.ShowContactInfo && props.ShowContactLocation) &&
                                <div className='dealer-contact__location'>
                                    <Typography variant='body2' className='dealer-contact__location'>{props.ContactLocation}</Typography></div>}
                            {props.TotalItemsSold > 0 &&
                                <div>
                                    <Typography
                                        variant='body2'
                                        className='dealer-contact__items-sold'
                                    >
                                    {props.TotalItemsSold} <TranslationText tKey='sandhills.tradepubwebsite.mvc.itemssold' />
                                    </Typography>
                                </div>
                            } 
                            {props.WebSite &&
                                <>
                                <ThemedLink variant='text' size='small' href={props.WebSite} target='_blank' rel='noopener' underline='hover' className='dealer-contact__website' sx={{ display: 'flex', alignItems: 'center' }} aria-label={translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite').toLowerCase() + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                        <span className='dealer-contact__website-text'>{translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite').toLowerCase()}</span>
                                        <Icon baseClassName='fas' className='fa-external-link-alt dealer-contact__website-icon' />
                                    </ThemedLink>
                                </>}
                        </div>
                        <div className='dealer-contact__details_right-col'>
                            {props.isAuction && props.ShowSendAMessage && 
                                <div>
                                    <ThemedLinkButton variant='text' size='small' className={'dealer-contact__button'} {...(ref?.current ? { ref: (element) => ref.current[inquiryType.Email] = element } : {})} onClick={() => props.openEmailModal(inquiryType.Email, props.contactType, ref.current[inquiryType.Email])}>
                                        <Icon baseClassName='fas' className='fa-envelope dealer-contact__icon' sx={{ color: 'contrastDefault.main' }} />
                                        {translator.translate('sandhills.tradesites.mvc.emailauctioneer')}
                                        <Icon baseClassName='fas' className='fa-external-link-alt' />
                                    </ThemedLinkButton>
                                </div>}
                            {props.ShowContactInfo && props.ShowDealerPhoneNumber && props.UnformattedPhone &&
                                <div className='dealer-contact__phone'>
                                    <PhoneLink {...props}
                                        Manufacturer={props.manufacturer}
                                        Model={props.model}
                                        AltCategoryID={props.AltCategoryID}
                                        CategoryID={props.categoryID}
                                        ListingType={props.listingType}
                                        ListingID={props.listingID}
                                        PartID={props.partID}
                                        Condition={props.condition}
                                        ShowIcon={true}
                                        isMobile={props.windowSize.isMobile}
                                    />
                                </div>}
                            {props.ShowContactInfo &&
                                <>
                                    {props.FBMessengerLink &&
                                        <div className='dealer-contact__messenger'>
                                            <PhoneLink {...props}
                                                IsFBMessenger={true}
                                                ShowIcon={true}
                                                isMobile={props.windowSize.isMobile}
                                            />
                                        </div>}
                                    {props.WhatsAppLink &&
                                        <div className='dealer-contact__whatsapp'>
                                            <PhoneLink {...props}
                                                IsWhatsApp={true}
                                                ShowIcon={true}
                                                isMobile={props.windowSize.isMobile}
                                            />
                                        </div>}
                                    {(props.contactType == contactType.Wholesale || props.contactType == contactType.Rental) && props.ShowSendAMessage && 
                                        <div>
                                            <ThemedLinkButton variant='text' size='small' className={'dealer-contact__button'} {...(ref?.current ? { ref: (element) => ref.current[inquiryType.Email] = element } : {})} onClick={() => props.openEmailModal(inquiryType.Email, props.contactType, ref.current[inquiryType.Email], props.IsWholesale, props.IsPreAT)}>
                                                <Icon baseClassName='fas' className='fa-envelope dealer-contact__icon' sx={{ color: 'contrastDefault.main' }} />
                                                {props.contactType == contactType.Rental ? <TranslationText tKey='sandhills.email' /> : <TranslationText tKey='sandhills.emailseller' />}
                                                <Icon baseClassName='fas' className='fa-external-link-alt' />
                                            </ThemedLinkButton>
                                        </div>}
                                    {props.ShowVideoChat &&
                                        <div>
                                            <ThemedLinkButton variant='text' size='small' className={'dealer-contact__videochat'} {...(ref?.current ? { ref: (element) => ref.current[inquiryType.VideoChat] = element } : {})} onClick={() => props.openEmailModal(inquiryType.VideoChat, props.contactType, ref.current[inquiryType.VideoChat], props.IsWholesale, props.IsPreAT)}>
                                                <Icon baseClassName='fas' className='fa-video dealer-contact__icon' sx={{ color: 'contrastDefault.main' }} />
                                                <TranslationText tKey='sandhills.videochat' />
                                                <Icon baseClassName='fas' className='fa-external-link-alt' />
                                            </ThemedLinkButton>
                                        </div>}

                                </>}
                        </div>
                    </div>

                    {!props.isPrintPage &&
                        <div className='dealer-contact__buttons'>
                            <CollapsibleContacts {...props}
                                onOpenInquiryModal={() => props.openEmailModal(inquiryType.Email, props.contactType, ref.current['contactOptions-' + inquiryType.Email])}
                                onMakeOffer={() => props.openEmailModal(inquiryType.Offer, props.contactType)}
                                onCheckAvailability={() => props.openEmailModal(inquiryType.CheckAvailability, props.contactType, ref.current['contactOptions-' + inquiryType.CheckAvailability])}
                                onVideoChat={() => props.openEmailModal(inquiryType.VideoChat, props.contactType, ref.current['contactOptions-' + inquiryType.VideoChat])}
                                onPhoneClickToTrack={phoneClickToTrack}
                                CustomEmailSellerTextForRental={props.CustomEmailSellerTextForRental}
                                CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                isMobile={props.windowSize.isMobile}
                                isDetailPage
                                ref={ref} />
                        </div>}
                </div>
            </ThemedBox>
        </ThemeProvider>
    );
});

export default DealerInfo;